(() => {
  "use strict";

  const PLANTS = [
    {
      id: "tomato",
      name: "Paradižnik",
      icon: "🍅",
      area: 0.35,
      inRow: 50,
      betweenRows: 70,
      spacing: "50 cm",
      zone: "north",
      support: true,
      tip: "Vodite ga ob opori, redno odstranjujte zalistnike in zračite, da se listi hitreje osušijo."
    },
    {
      id: "pepper",
      name: "Paprika",
      icon: "🫑",
      area: 0.25,
      inRow: 50,
      betweenRows: 50,
      spacing: "50 cm",
      zone: "middle",
      support: false,
      tip: "Sadite jo na toplejši, dobro osvetljen del in ohranjajte enakomerno vlažna tla."
    },
    {
      id: "cucumber",
      name: "Kumare",
      icon: "🥒",
      area: 0.50,
      inRow: 50,
      betweenRows: 100,
      spacing: "50 cm",
      zone: "north",
      support: true,
      tip: "Vzgoja ob opori prihrani prostor. Zalivajte pri tleh in redno obirajte plodove."
    },
    {
      id: "eggplant",
      name: "Jajčevec",
      icon: "🍆",
      area: 0.28,
      inRow: 40,
      betweenRows: 70,
      spacing: "40 cm",
      zone: "middle",
      support: false,
      tip: "Potrebuje veliko toplote in svetlobe; ne postavljajte ga v senco visokih kultur."
    },
    {
      id: "pumpkin",
      name: "Bučke",
      icon: "🎃",
      area: 1.00,
      inRow: 100,
      betweenRows: 100,
      spacing: "100 cm",
      zone: "middle",
      support: false,
      tip: "Za eno rastlino rezervirajte več prostora ob robu grede, da listi ne zaprejo prehoda."
    },
    {
      id: "melon",
      name: "Melone",
      icon: "🍈",
      area: 0.50,
      inRow: 50,
      betweenRows: 100,
      spacing: "50 cm",
      zone: "north",
      support: true,
      tip: "Načrt predvideva navpično vzgojo ob opori. Pri plodovih uporabite mrežasto oporo; rast po tleh zahteva več prostora."
    },
    {
      id: "lettuce",
      name: "Solata",
      icon: "🥬",
      area: 0.04,
      inRow: 20,
      betweenRows: 20,
      spacing: "20 cm",
      zone: "south",
      support: false,
      tip: "Uporabite robove gred in zgodnejše ali poznejše termine, saj v poletni vročini hitro uhaja v cvet."
    },
    {
      id: "strawberry",
      name: "Jagode",
      icon: "🍓",
      area: 0.075,
      inRow: 25,
      betweenRows: 30,
      spacing: "25–30 cm",
      zone: "south",
      support: false,
      tip: "Poskrbite za zračen sklop in suhe plodove; odstranite poškodovane liste in plodove."
    },
    {
      id: "basil",
      name: "Bazilika",
      icon: "🌿",
      area: 0.09,
      inRow: 30,
      betweenRows: 30,
      spacing: "30 cm",
      zone: "south",
      support: false,
      tip: "Sadite jo na svetel rob grede in redno vršičkajte, da se rastlina razveji."
    }
  ];

  const DEFAULT_QUANTITIES = {
    tomato: 8,
    pepper: 0,
    cucumber: 0,
    eggplant: 0,
    pumpkin: 0,
    melon: 0,
    lettuce: 0,
    strawberry: 0,
    basil: 0
  };

  const bySelector = (root, selector) => root.querySelector(selector);
  const allBySelector = (root, selector) => Array.from(root.querySelectorAll(selector));
  const parseJson = (root, selector, fallback) => {
    try {
      return JSON.parse(bySelector(root, selector)?.textContent || "");
    } catch {
      return fallback;
    }
  };
  const numberValue = (value) => {
    if (typeof value === "number") return value;
    if (typeof value !== "string") return 0;
    return Number(value.replace(",", ".").replace(/[^\d.-]/g, "")) || 0;
  };
  const formatNumber = (value, decimals = 1) =>
    new Intl.NumberFormat("sl-SI", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    }).format(value);
  const formatMoney = (cents) =>
    new Intl.NumberFormat("sl-SI", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
      maximumFractionDigits: 2
    }).format(cents / 100);
  const escapeHtml = (value) =>
    String(value ?? "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;");

  /*
   * Some Shopify shipping-bar apps are injected through content_for_header and
   * therefore do not exist in the exported theme files. On the calculator page
   * we remove only a full-width bar that actually says “FREE DELIVERY”. This
   * keeps the promotion on every other storefront page and also catches bars
   * injected after the page has loaded.
   */
  const suppressExternalDeliveryBar = () => {
    if (!document.querySelector("[data-td-calculator]")) return;

    let scanScheduled = false;
    const deliveryText = /\bFREE\s+DELIVERY\b/i;
    const bannerName = /(?:free.?shipping|free.?delivery|shipping.?bar|delivery.?bar|announcement|promo.?bar|\bfsb\b)/i;
    const viewportWidth = () =>
      Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
    const isOrange = (color) => {
      const match = String(color || "").match(/rgba?\(\s*(\d+)[,\s]+(\d+)[,\s]+(\d+)/i);
      if (!match) return false;
      const [, red, green, blue] = match.map(Number);
      return red >= 210 && green >= 45 && green <= 155 && blue <= 65;
    };
    const hide = (element) => {
      if (!element || element.closest("[data-td-calculator]")) return;
      element.dataset.tdDeliveryBarHidden = "true";
      element.style.setProperty("display", "none", "important");
      element.style.setProperty("visibility", "hidden", "important");
      element.style.setProperty("height", "0", "important");
      element.style.setProperty("min-height", "0", "important");
      element.style.setProperty("pointer-events", "none", "important");
    };
    const looksLikeBar = (element) => {
      const rect = element.getBoundingClientRect();
      if (
        rect.width < Math.max(320, viewportWidth() * 0.72) ||
        rect.height < 24 ||
        rect.height > 240
      ) {
        return false;
      }
      const style = window.getComputedStyle(element);
      const identity = `${element.id || ""} ${element.className || ""}`;
      return (
        style.position === "fixed" ||
        style.position === "sticky" ||
        bannerName.test(identity) ||
        isOrange(style.backgroundColor)
      );
    };
    const hideKnownBars = () => {
      document
        .querySelectorAll(
          [
            "#fsb_container",
            "#fsb_bar",
            ".fsb_background",
            '[id*="free-shipping" i]',
            '[class*="free-shipping" i]',
            '[id*="free-delivery" i]',
            '[class*="free-delivery" i]',
            '[id*="shipping-bar" i]',
            '[class*="shipping-bar" i]'
          ].join(",")
        )
        .forEach(hide);
    };
    const scan = () => {
      scanScheduled = false;
      hideKnownBars();

      document.querySelectorAll("body *").forEach((element) => {
        if (
          element.closest("[data-td-calculator]") ||
          element.dataset.tdDeliveryBarHidden === "true"
        ) {
          return;
        }
        const ownText = Array.from(element.childNodes)
          .filter((node) => node.nodeType === Node.TEXT_NODE)
          .map((node) => node.textContent || "")
          .join(" ")
          .replace(/\s+/g, " ")
          .trim();
        const accessibleText = [
          ownText,
          element.getAttribute("aria-label") || "",
          element.getAttribute("title") || ""
        ].join(" ");
        if (!deliveryText.test(accessibleText)) return;

        let candidate = element;
        for (let depth = 0; candidate && candidate !== document.body && depth < 9; depth += 1) {
          if (looksLikeBar(candidate)) {
            hide(candidate);
            break;
          }
          candidate = candidate.parentElement;
        }
      });

      document.querySelectorAll("body *").forEach((element) => {
        if (
          element.closest("[data-td-calculator]") ||
          element.dataset.tdDeliveryBarHidden === "true"
        ) {
          return;
        }
        const style = window.getComputedStyle(element);
        if (
          (style.position === "fixed" || style.position === "sticky") &&
          isOrange(style.backgroundColor) &&
          looksLikeBar(element)
        ) {
          hide(element);
        }
      });
    };
    const scheduleScan = () => {
      if (scanScheduled) return;
      scanScheduled = true;
      window.requestAnimationFrame(scan);
    };

    scan();
    const observer = new MutationObserver(scheduleScan);
    observer.observe(document.body, { childList: true, subtree: true });
    [250, 750, 1500, 3000, 6000].forEach((delay) =>
      window.setTimeout(scheduleScan, delay)
    );
    window.setTimeout(() => observer.disconnect(), 15000);
  };

  const findDimensions = (raw) => {
    const text = String(raw || "").replaceAll(",", ".");
    const matches = Array.from(
      text.matchAll(
        /(\d+(?:\.\d+)?)\s*(m|cm|ft|')?\s*[x×]\s*(\d+(?:\.\d+)?)\s*(m|cm|ft|')?/gi
      )
    )
      .map((match) => {
        let width = Number(match[1]);
        let length = Number(match[3]);
        const firstUnit = String(match[2] || "").toLowerCase();
        const secondUnit = String(match[4] || "").toLowerCase();
        const units = `${firstUnit} ${secondUnit}`;

        if (units.includes("cm")) {
          width /= 100;
          length /= 100;
        } else if (/(?:ft|')/.test(units)) {
          width *= 0.3048;
          length *= 0.3048;
        } else if (!units.includes("m") && width >= 6 && length >= 6) {
          // Hišni modeli (npr. 6x8, 8x12) imajo mere praviloma zapisane v čevljih.
          width *= 0.3048;
          length *= 0.3048;
        }

        return {
          width: Math.round(width * 100) / 100,
          length: Math.round(length * 100) / 100,
          hasMetricUnit: units.includes("m") && !units.includes("cm")
        };
      })
      .filter(
        ({ width, length }) =>
          Number.isFinite(width) &&
          Number.isFinite(length) &&
          width >= 0.8 &&
          width <= 15 &&
          length >= 1.5 &&
          length <= 60
      );
    if (!matches.length) return null;
    const metricMatches = matches.filter((match) => match.hasMetricUnit);
    const chosen = metricMatches[metricMatches.length - 1] || matches[matches.length - 1];
    return { width: chosen.width, length: chosen.length };
  };

  const findThickness = (raw, fallback) => {
    if (numberValue(fallback) === 4 || numberValue(fallback) === 6) {
      return numberValue(fallback);
    }
    const text = Array.isArray(raw) ? raw.join(" ") : String(raw || "");
    const matches = Array.from(text.matchAll(/(?:^|\D)(4|6)\s*mm/gi));
    return matches.length ? Number(matches[matches.length - 1][1]) : 0;
  };

  const cleanModelName = (raw, fallback) => {
    if (raw) return String(raw).trim();
    const title = String(fallback || "");
    if (/professional farmer/i.test(title)) return "PROFESSIONAL FARMER";
    if (/victoria delux/i.test(title)) return "VICTORIA DELUX";
    if (/comfort double/i.test(title)) return "COMFORT DOUBLE";
    if (/titan plus/i.test(title)) return "TITAN PLUS";
    if (/titan/i.test(title)) return "TITAN";
    if (/vrtni/i.test(title)) return "VRTNI";
    if (/comfort pro/i.test(title)) return "COMFORT PRO";
    if (/wall[\s-]*mounted/i.test(title)) return "STENSKI";
    if (/\bhybrid\b/i.test(title)) return "HYBRID";
    if (/\bglory\b/i.test(title)) return "GLORY";
    if (/\bbella\b/i.test(title)) return "BELLA";
    if (/\bessenc?e\b/i.test(title)) return "ESSENCE";
    if (/euro[\s-]*klasika/i.test(title)) return "EURO KLASIKA";
    if (/\b(?:tube|tuba)\b/i.test(title)) return "TUBE";
    if (/\beasy\b/i.test(title)) return "EASY";
    if (/\bstark\b/i.test(title)) return "STARK";
    if (/\bstandar[dt]\b/i.test(title)) return "STANDART";
    if (/(?:širine|sirine|width)\s*2[.,]4\s*m/i.test(title)) return "VRTNI 2,4 m";
    if (/(?:širine|sirine|width)\s*2\s*m/i.test(title)) return "VRTNI 2 m";
    return (
      title
        .split("|")[0]
        .replace(/\d+(?:[.,]\d+)?\s*m?\s*[x×]\s*\d+(?:[.,]\d+)?\s*m?/gi, "")
        .replace(/\b(?:4|6)\s*mm\b/gi, "")
        .replace(/\b(?:polycarbonate|polikarbonat|width|širina|širine|sirina|sirine|garden|greenhouse|rastlinjak)\b/gi, "")
        .replace(/\s{2,}/g, " ")
        .replace(/^[\s,;:/-]+|[\s,;:/-]+$/g, "")
        .trim() || "Rastlinjak"
    );
  };

  const normalizeCatalog = (rawCatalog) => {
    const variants = rawCatalog
      .map((item) => {
        if (
          /\b(?:modul|module|extension|podaljšek|podaljsek|roof window|strešno okno|stresno okno|automatic opener|avtomatski odpirač|shelf)\b/i.test(
            String(item.productTitle || "")
          )
        ) {
          return null;
        }
        const options = Array.isArray(item.options) ? item.options : [];
        const joined = [options.join(" "), item.variantTitle, item.productTitle].join(" ");
        const dimensions =
          numberValue(item.widthMeta) > 0 && numberValue(item.lengthMeta) > 0
            ? { width: numberValue(item.widthMeta), length: numberValue(item.lengthMeta) }
            : findDimensions(joined);
        const thicknessOption = options.find((option) => /(?:^|\D)(?:4|6)\s*mm/i.test(option));
        const thickness = findThickness(
          thicknessOption || joined,
          item.thicknessMeta
        );
        if (!dimensions || !item.available) return null;
        return {
          ...item,
          ...dimensions,
          thickness: [4, 6].includes(thickness) ? thickness : 0,
          area: dimensions.width * dimensions.length,
          modelName: cleanModelName(item.modelName, item.productTitle),
          isPink: /rožnat|pink/i.test([item.variantTitle, ...options].join(" ")),
          highlight:
            item.highlight ||
            (dimensions.width <= 2.4
              ? "Kompaktna izbira za ožji vrt"
              : dimensions.width <= 3
                ? "Praktična širina za različne kulture"
                : "Več prostora za večji pridelek")
        };
      })
      .filter(Boolean);

    const grouped = new Map();
    variants.forEach((variant) => {
      const key = `${variant.modelName.toLocaleLowerCase("sl-SI")}-${variant.width}-${variant.length}`;
      if (!grouped.has(key)) {
        grouped.set(key, {
          id: key,
          name: variant.modelName,
          width: variant.width,
          length: variant.length,
          area: variant.area,
          image: variant.image,
          highlight: variant.highlight,
          frameSpacing: numberValue(variant.frameSpacing),
          windowUrl: variant.windowUrl || "",
          variants: {}
        });
      }
      const model = grouped.get(key);
      const current = model.variants[variant.thickness];
      if (!current || (current.isPink && !variant.isPink)) {
        model.variants[variant.thickness] = variant;
      }
    });

    return Array.from(grouped.values())
      .filter((model) => model.variants[4] || model.variants[6] || model.variants[0])
      .sort(
        (a, b) =>
          a.area - b.area ||
          a.width - b.width ||
          a.length - b.length ||
          minimumPrice(a) - minimumPrice(b)
      );
  };

  const minimumPrice = (model) =>
    Math.min(
      ...[
        model.variants[4]?.priceCents,
        model.variants[6]?.priceCents,
        model.variants[0]?.priceCents
      ].filter(
        (price) => Number.isFinite(price) && price > 0
      )
    );

  const bestVariant = (model, preferred = 6) =>
    model.variants[preferred] || model.variants[6] || model.variants[4] || model.variants[0];

  const sizeLabel = (model) =>
    `${formatNumber(model.width, model.width % 1 ? 1 : 0)} × ${formatNumber(
      model.length,
      model.length % 1 ? 1 : 0
    )} m`;

  const countLabel = (count, one, two, threeFour, many) => {
    if (count === 1) return `${count} ${one}`;
    if (count === 2) return `${count} ${two}`;
    if (count === 3 || count === 4) return `${count} ${threeFour}`;
    return `${count} ${many}`;
  };
  const windowCountLabel = (count) =>
    countLabel(count, "strešno okno", "strešni okni", "strešna okna", "strešnih oken");
  const plantCountLabel = (count) =>
    countLabel(count, "rastlina", "rastlini", "rastline", "rastlin");
  const seedlingCountLabel = (count) =>
    countLabel(count, "sadika", "sadiki", "sadike", "sadik");
  const bedCountLabel = (count) =>
    countLabel(count, "greda", "gredi", "grede", "gred");
  const pathCountLabel = (count) =>
    countLabel(count, "prehod", "prehoda", "prehodi", "prehodov");
  const thicknessLabel = (variant) =>
    variant.thickness
      ? `${variant.thickness} mm${variant.isPink ? " rožnati" : ""}`
      : "Kritina vključena";

  const ventilationForModel = (model) => {
    const isStandart = /\bstandar[dt]\b/i.test(model.name);
    if (isStandart) {
      return {
        kind: "included",
        extraWindows: 0,
        title: "Strešna okna so že vključena",
        description:
          "Model STANDART ima strešna okna že v kompletu, zato dodatnega okna ni treba kupiti.",
        summary: "strešna okna so vključena v kompletu; dodatna niso potrebna",
        openerAvailable: true,
        showWindowLink: false
      };
    }
    if (model.length >= 6) {
      return {
        kind: "recommended",
        extraWindows: 1,
        title: "Priporočamo vsaj 1 dodatno strešno okno",
        description:
          "Pri rastlinjakih dolžine 6 m ali več dodatno strešno okno izboljša odvajanje toplote in vlage.",
        summary: "priporočamo vsaj 1 dodatno strešno okno",
        openerAvailable: true,
        showWindowLink: true
      };
    }
    return {
      kind: "doors",
      extraWindows: 0,
      title: "Dodatno strešno okno ni potrebno",
      description:
        "Pri krajšem rastlinjaku za osnovno prezračevanje praviloma zadoščajo vrata na obeh straneh.",
      summary: "vrata na obeh straneh praviloma zadoščajo; dodatno okno ni potrebno",
      openerAvailable: false,
      showWindowLink: false
    };
  };

  const modelCard = (model, options = {}) => {
    const variant = bestVariant(model, options.thickness || 6);
    const priceRows = [4, 6, 0]
      .filter((thickness) => model.variants[thickness])
      .map(
        (thickness) =>
          `<span><small>${thicknessLabel(model.variants[thickness])}</small><strong>${formatMoney(
            model.variants[thickness].priceCents
          )}</strong></span>`
      )
      .join("");
    const reserve =
      Number.isFinite(options.requiredArea) && options.requiredArea > 0
        ? Math.max(0, model.area - options.requiredArea)
        : null;
    return `
      <article
        class="td-model-card ${options.compact ? "td-model-card--compact" : ""} ${options.selected ? "is-selected" : ""}"
        data-model-id="${escapeHtml(model.id)}"
        ${options.selectable && options.compact ? `data-select-model="${escapeHtml(model.id)}" tabindex="0" role="radio" aria-checked="${Boolean(options.selected)}"` : ""}
      >
        ${options.badge ? `<em>${escapeHtml(options.badge)}</em>` : ""}
        ${options.compact ? `<span class="td-model-radio" aria-hidden="true"></span>` : ""}
        ${
          model.image
            ? `<img src="${escapeHtml(model.image)}" alt="${escapeHtml(model.name)} ${escapeHtml(sizeLabel(model))}" loading="lazy">`
            : `<div class="td-model-placeholder" aria-hidden="true"><i></i></div>`
        }
        <div class="td-model-card-copy">
          <small>${escapeHtml(model.name)}</small>
          <h3>${escapeHtml(sizeLabel(model))}</h3>
          <p>${escapeHtml(model.highlight)}</p>
          ${options.compact ? `<small class="td-model-area">${formatNumber(model.area)} m²</small>` : ""}
          ${
            reserve !== null
              ? `<span class="td-reserve">+${formatNumber(reserve)} m² prostora za prihodnje rastline</span>`
              : ""
          }
        </div>
        <div class="td-model-price" aria-label="Cene razpoložljivih debelin polikarbonata">
          ${priceRows}
        </div>
        ${
          options.selectable && !options.compact
            ? `<button type="button" data-select-model="${escapeHtml(model.id)}">${
                options.selected ? "Izbrano" : "Izberi"
              }</button>`
            : options.selectable
              ? ""
              : `<a href="${escapeHtml(variant.url)}" target="_blank" rel="noopener">Poglej model ↗</a>`
        }
      </article>
    `;
  };

  const initialize = (root) => {
    const rawCatalog = parseJson(root, "[data-td-catalog]", []);
    const featuredRules = parseJson(root, "[data-td-featured]", []);
    const settings = parseJson(root, "[data-td-settings]", {});
    const catalog = normalizeCatalog(rawCatalog);
    const quantities = { ...DEFAULT_QUANTITIES };
    const state = {
      mode: "quick",
      quickStep: 1,
      quickArea: 8,
      quickSpace: "",
      quickMaxArea: 0,
      quickCapacityShortfall: false,
      quickSpaceShortfall: false,
      exactStep: 1,
      budget: 150000,
      noBudget: true,
      widthLimit: 0,
      lengthLimit: 0,
      budgetLimit: 0,
      showAllModels: false,
      selectedModelId: "",
      selectedThickness: 6,
      autoOpeners: false
    };

    const live = bySelector(root, "[data-td-live]");
    const announce = (message) => {
      if (live) live.textContent = message;
    };

    const scrollToPanel = () => {
      const panel = bySelector(root, ".td-calculator-panel");
      if (!panel) return;
      const stickyHeader = document.querySelector(
        ".t4s-section-header, #t4s-header, header"
      );
      const headerHeight = stickyHeader
        ? Math.min(stickyHeader.getBoundingClientRect().height, 150)
        : 0;
      const top =
        panel.getBoundingClientRect().top +
        window.scrollY -
        headerHeight -
        18;
      window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
    };

    const setMode = (mode) => {
      if (mode === state.mode) return;
      const exactStarted =
        state.exactStep > 1 ||
        Object.entries(quantities).some(([id, value]) => value !== DEFAULT_QUANTITIES[id]);
      if (state.mode === "exact" && exactStarted) {
        const shouldSwitch = window.confirm(
          "Vaš natančen izračun bo ostal shranjen. Želite odpreti hitro priporočilo?"
        );
        if (!shouldSwitch) return;
      }
      state.mode = mode;
      root.classList.toggle("is-final-layout", mode === "exact" && state.exactStep === 4);
      bySelector(root, "[data-td-quick]").hidden = mode !== "quick";
      bySelector(root, "[data-td-exact]").hidden = mode !== "exact";
      allBySelector(root, "[data-td-mode]").forEach((button) => {
        const active = button.dataset.tdMode === mode;
        button.classList.toggle("is-active", active);
        button.setAttribute("aria-selected", String(active));
        button.tabIndex = active ? 0 : -1;
      });
      announce(mode === "quick" ? "Odprto je hitro priporočilo." : "Odprt je natančen izračun.");
    };

    const showQuickStep = (step) => {
      state.quickStep = step;
      allBySelector(root, "[data-td-quick-step]").forEach((section) => {
        section.classList.toggle("is-active", Number(section.dataset.tdQuickStep) === step);
      });
      bySelector(root, "[data-td-quick-progress]").textContent =
        step >= 3 ? "Rezultat" : `${step} od 2`;
      bySelector(root, "[data-td-quick-bar]").style.width =
        step === 1 ? "50%" : "100%";
      if (step > 1) scrollToPanel();
    };

    const showExactStep = (step) => {
      state.exactStep = step;
      root.classList.toggle("is-final-layout", step === 4);
      allBySelector(root, "[data-td-exact-step]").forEach((section) => {
        section.classList.toggle("is-active", Number(section.dataset.tdExactStep) === step);
      });
      bySelector(root, "[data-td-exact-progress]").textContent = `${step} od 4`;
      bySelector(root, "[data-td-exact-bar]").style.width = `${step * 25}%`;
      scrollToPanel();
    };

    const renderPlants = () => {
      const container = bySelector(root, "[data-td-plants]");
      container.innerHTML = PLANTS.map(
        (plant) => `
          <article class="${quantities[plant.id] > 0 ? "is-selected" : ""}" data-plant="${plant.id}">
            <span class="td-plant-icon">${plant.icon}</span>
            <b>${plant.name}</b>
            <div>
              <button type="button" aria-label="Odstrani ${plant.name}" data-plant-minus="${plant.id}">−</button>
              <input
                type="number"
                min="0"
                max="500"
                step="1"
                inputmode="numeric"
                aria-label="Količina: ${plant.name}"
                value="${quantities[plant.id]}"
                data-plant-input="${plant.id}"
              >
              <button type="button" aria-label="Dodaj ${plant.name}" data-plant-plus="${plant.id}">+</button>
            </div>
          </article>
        `
      ).join("");
    };

    const renderMethodTable = () => {
      const container = bySelector(root, "[data-td-method-table]");
      if (!container) return;
      container.innerHTML = PLANTS.map(
        (plant) => `
          <tr>
            <td><span aria-hidden="true">${plant.icon}</span>${plant.name}</td>
            <td>${plant.inRow} cm</td>
            <td>${plant.betweenRows} cm</td>
            <td><strong>${formatNumber(plant.area, 2)} m²</strong>/sadiko</td>
          </tr>
        `
      ).join("");
    };

    const areaCalculation = () => {
      const plantedArea = PLANTS.reduce(
        (sum, plant) => sum + plant.area * (quantities[plant.id] || 0),
        0
      );
      const passageArea = plantedArea > 0 ? Math.max(1.2, plantedArea * 0.38) : 0;
      return { plantedArea, passageArea, requiredArea: plantedArea + passageArea };
    };

    const updateAreaSummary = () => {
      const calculation = areaCalculation();
      allBySelector(root, "[data-td-plant-area]").forEach((item) => {
        item.textContent = `${formatNumber(calculation.plantedArea)} m²`;
      });
      allBySelector(root, "[data-td-passage-area]").forEach((item) => {
        item.textContent = `${formatNumber(calculation.passageArea)} m²`;
      });
      allBySelector(root, "[data-td-required-area]").forEach((item) => {
        item.textContent = `${formatNumber(calculation.requiredArea)} m²`;
      });
      renderPlanPreview();
    };

    const setPlantQuantity = (id, value) => {
      quantities[id] = Math.max(0, Math.min(500, Math.round(numberValue(value))));
      const input = bySelector(root, `[data-plant-input="${id}"]`);
      if (input) input.value = quantities[id];
      bySelector(root, `[data-plant="${id}"]`)?.classList.toggle("is-selected", quantities[id] > 0);
      updateAreaSummary();
    };

    const updatePlant = (id, delta) => {
      setPlantQuantity(id, (quantities[id] || 0) + delta);
    };

    const modelsForQuick = () => {
      if (!catalog.length) return [];
      state.quickCapacityShortfall = false;
      state.quickSpaceShortfall = false;
      if (state.quickSpace === "unknown") {
        const defaultModel =
          catalog.find((model) => model.width === 2 && model.length === 6) ||
          catalog.find((model) => model.width === 2.4 && model.length === 6) ||
          catalog[Math.floor((catalog.length - 1) / 2)];
        return [
          defaultModel,
          ...catalog.filter((model) => model.id !== defaultModel.id)
        ].slice(0, 3);
      }
      const maximum = state.quickMaxArea || Number.POSITIVE_INFINITY;
      const eligible = catalog.filter((model) => model.area <= maximum);
      const pool = eligible.length ? eligible : [catalog[0]];
      state.quickSpaceShortfall =
        !eligible.length &&
        Number.isFinite(maximum) &&
        catalog[0].area > maximum;
      const suitable = pool.filter((model) => model.area >= state.quickArea);
      const recommendation = suitable[0] || pool[pool.length - 1];
      state.quickCapacityShortfall = suitable.length === 0 && recommendation.area < state.quickArea;
      return [
        recommendation,
        ...pool
          .filter((model) => model.id !== recommendation.id)
          .sort((a, b) => Math.abs(a.area - recommendation.area) - Math.abs(b.area - recommendation.area))
      ].slice(0, 3);
    };

    const renderQuickResults = () => {
      const container = bySelector(root, "[data-td-quick-results]");
      if (!catalog.length) {
        container.innerHTML = setupMessage();
        return;
      }
      const models = modelsForQuick();
      bySelector(root, "[data-td-quick-note]").textContent =
        state.quickSpace === "unknown"
          ? "Priporočamo za začetek — velikost lahko pozneje prilagodite ali razširite."
          : state.quickSpaceShortfall
            ? "V trenutnem katalogu ni rastlinjaka, ki bi se prilegal izbranemu prostoru. Prikazujemo najmanjši dejanski model; pred nakupom preverite razpoložljive mere vrta."
          : state.quickCapacityShortfall
            ? "Izbrani prostor je manjši od okvirne potrebe za želeno količino. Prikazujemo največjo možnost, ki še ustreza prostoru; za natančnejši kompromis odprite natančen izračun."
          : "Prvi model je najmanjša primerna možnost glede na vaše odgovore. Preverite tudi druge prikazane variante.";
      container.innerHTML = models
        .map((model, index) =>
          modelCard(model, {
            badge: index === 0 ? "Najmanjša primerna izbira" : ""
          })
        )
        .join("");
    };

    const setupMessage = () => `
      <div class="td-setup-message">
        <b>Kolekcija za kalkulator še ni izbrana.</b>
        <p>V urejevalniku teme izberite slovensko kolekcijo rastlinjakov. Kalkulator bo nato sam prebral razpoložljive variante in cene.</p>
      </div>
    `;

    const populateFilters = () => {
      const widths = Array.from(new Set(catalog.map((model) => model.width))).sort((a, b) => a - b);
      const lengths = Array.from(new Set(catalog.map((model) => model.length))).sort((a, b) => a - b);
      const widthSelect = bySelector(root, "[data-td-width-filter]");
      const lengthSelect = bySelector(root, "[data-td-length-filter]");
      widths.forEach((value) => {
        widthSelect.insertAdjacentHTML(
          "beforeend",
          `<option value="${value}">Največ ${formatNumber(value, value % 1 ? 1 : 0)} m</option>`
        );
      });
      lengths.forEach((value) => {
        lengthSelect.insertAdjacentHTML(
          "beforeend",
          `<option value="${value}">Največ ${formatNumber(value, value % 1 ? 1 : 0)} m</option>`
        );
      });
    };

    const eligibleModels = () => {
      const required = areaCalculation().requiredArea;
      const fitByArea = catalog.filter((model) => model.area >= required);
      return fitByArea.filter(
        (model) =>
          (!state.widthLimit || model.width <= state.widthLimit) &&
          (!state.lengthLimit || model.length <= state.lengthLimit) &&
          (!state.budgetLimit || minimumPrice(model) <= state.budgetLimit)
      );
    };

    const renderModelResults = () => {
      const container = bySelector(root, "[data-td-model-results]");
      if (!catalog.length) {
        container.innerHTML = setupMessage();
        return;
      }
      const required = areaCalculation().requiredArea;
      const fitByArea = catalog.filter((model) => model.area >= required);
      if (!fitByArea.length) {
        const largest = catalog[catalog.length - 1];
        container.innerHTML = `
          <div class="td-setup-message">
            <b>En rastlinjak iz trenutnega kataloga ni dovolj velik za izbrano količino.</b>
            <p>Največji razpoložljivi model je ${escapeHtml(sizeLabel(largest))}. Zmanjšajte količino rastlin ali nas pokličite za načrt z več rastlinjaki. Kalkulator ne bo predlagal velikosti, ki je ni v katalogu.</p>
          </div>
        `;
        state.selectedModelId = "";
        return;
      }
      let models = eligibleModels();
      if (!models.length) {
        container.innerHTML = `
          <div class="td-setup-message">
            <b>Za izbrane omejitve ni primernega modela.</b>
            <p>Spremenite širino, dolžino ali proračun. Kalkulator ne bo predlagal velikosti, ki je ni v katalogu.</p>
          </div>
        `;
        state.selectedModelId = "";
        return;
      }
      if (!models.some((model) => model.id === state.selectedModelId)) {
        state.selectedModelId = models[0].id;
      }
      const visibleModels = state.showAllModels ? models : models.slice(0, 3);
      const toggle =
        models.length > 3
          ? `<button type="button" class="td-model-toggle" data-td-toggle-models aria-expanded="${state.showAllModels}">
              ${state.showAllModels ? "Prikaži manj ↑" : `Poglej vse primerne modele (${models.length}) ↓`}
            </button>`
          : "";
      container.innerHTML =
        toggle +
        '<p class="td-reserve-explainer"><b>Kaj pomeni dodatni prostor?</b> Po načrtovani zasaditvi ostane nekaj površine za nove rastline — na primer v naslednji sezoni.</p>' +
        visibleModels
          .map((model, index) =>
            modelCard(model, {
              badge: index === 0 ? "Najmanjši primeren model" : "",
              compact: true,
              requiredArea: required,
              selectable: true,
              selected: model.id === state.selectedModelId
            })
          )
          .join("");
      const continueButton = bySelector(root, "[data-td-to-equipment]");
      const currentModel = models.find((model) => model.id === state.selectedModelId);
      if (continueButton && currentModel) {
        continueButton.textContent = `Nadaljuj z ${currentModel.name} →`;
      }
    };

    const selectedModel = () =>
      catalog.find((model) => model.id === state.selectedModelId) || eligibleModels()[0] || null;

    const renderEquipment = () => {
      const model = selectedModel();
      const container = bySelector(root, "[data-td-equipment]");
      if (!model) {
        container.innerHTML = setupMessage();
        return;
      }
      const availableThicknesses = [4, 6, 0].filter((value) => model.variants[value]);
      if (!availableThicknesses.includes(state.selectedThickness)) {
        state.selectedThickness = availableThicknesses.includes(6)
          ? 6
          : availableThicknesses[0];
      }
      const ventilation = ventilationForModel(model);
      if (!ventilation.openerAvailable) state.autoOpeners = false;
      const windowUrl = model.windowUrl || settings.defaultWindowUrl || "";
      container.innerHTML = `
        <article>
          <p>IZBRANI RASTLINJAK</p>
          <h3>${escapeHtml(model.name)} ${escapeHtml(sizeLabel(model))}</h3>
          <span>Izberite razpoložljivo debelino. Prikazana cena je cena točno te variante Shopify.</span>
          <div class="td-thickness-options">
            ${availableThicknesses
              .map((value) => {
                const variant = model.variants[value];
                return `<button type="button" class="${state.selectedThickness === value ? "is-active" : ""}" data-thickness="${value}"><b>${thicknessLabel(variant)}</b><strong>${formatMoney(variant.priceCents)}</strong></button>`;
              })
              .join("")}
          </div>
        </article>
        <article>
          <p>PREZRAČEVANJE</p>
          <h3>${escapeHtml(ventilation.title)}</h3>
          <span>${escapeHtml(ventilation.description)}</span>
          ${
            ventilation.showWindowLink && windowUrl
              ? `<a href="${escapeHtml(windowUrl)}" target="_blank" rel="noopener">Poglej kompatibilno okno ↗</a>`
              : ventilation.showWindowLink
                ? `<small>Povezavo do kompatibilnega okna nastavite pri izdelku ali v nastavitvah sekcije.</small>`
                : ""
          }
        </article>
        ${
          ventilation.openerAvailable
            ? `<article>
                <p>UDOBJE</p>
                <label class="td-auto-opener">
                  <input type="checkbox" data-auto-opener ${state.autoOpeners ? "checked" : ""}>
                  <span><b>Dodaj avtomatsko odpiranje</b><small>${
                    ventilation.kind === "included"
                      ? "Za strešno okno, ki je že vključeno v kompletu."
                      : "Za priporočeno dodatno strešno okno."
                  }</small></span>
                </label>
                ${
                  settings.autoOpenerUrl
                    ? `<a href="${escapeHtml(settings.autoOpenerUrl)}" target="_blank" rel="noopener">Poglej avtomatski odpirač ↗</a>`
                    : ""
                }
              </article>`
            : ""
        }
      `;
    };

    const layoutForModel = (model) => {
      const bedCount = model.width <= 2.5 ? 2 : model.width <= 3.5 ? 3 : 4;
      const pathCount = bedCount - 1;
      const pathWidth = bedCount === 2 ? 0.6 : 0.5;
      const bedWidth = Math.max(
        0.5,
        (model.width - pathCount * pathWidth) / bedCount
      );
      return { bedCount, pathCount, pathWidth, bedWidth };
    };

    const plantingRows = (model) => {
      const chosen = PLANTS.filter((plant) => quantities[plant.id] > 0);
      const layout = layoutForModel(model);
      const groups =
        layout.bedCount === 2
          ? [
              {
                title: "SEVERNA GREDA",
                subtitle: "visoke rastline in plezalke ob opori",
                plants: chosen.filter((plant) => plant.zone === "north")
              },
              {
                title: "JUŽNA GREDA",
                subtitle: "nižje in toploljubne kulture",
                plants: chosen.filter((plant) => plant.zone !== "north")
              }
            ]
          : [
              {
                title: "SEVERNA GREDA",
                zone: "north",
                subtitle: "visoke rastline in plezalke ob opori",
                plants: chosen.filter((plant) => plant.zone === "north")
              },
              ...Array.from({ length: layout.bedCount - 2 }, (_, index) => ({
                title: `SREDNJA GREDA ${layout.bedCount > 3 ? index + 1 : ""}`.trim(),
                zone: `middle-${index + 1}`,
                subtitle: "toploljubne plodovke",
                plants: chosen
                  .filter((plant) => plant.zone === "middle")
                  .filter((_, plantIndex) => plantIndex % (layout.bedCount - 2) === index)
              })),
              {
                title: "JUŽNA GREDA",
                zone: "south",
                subtitle: "nižje kulture in robne zasaditve",
                plants: chosen.filter((plant) => plant.zone === "south")
              }
            ];
      if (layout.bedCount === 2) {
        groups[0].zone = "north";
        groups[1].zone = "south";
      }
      const row = (title, zone, subtitle, plants) => `
        <div class="td-bed td-bed--${zone}">
          <div><b>${title}</b><small>${subtitle}</small></div>
          <ul>
            ${
              plants.length
                ? plants
                    .map(
                      (plant) =>
                        `<li><span>${plant.icon}</span><b>${plant.name}</b><small>${seedlingCountLabel(quantities[plant.id])} • ${plant.spacing} narazen${plant.support ? " • ob opori" : ""}</small></li>`
                    )
                    .join("")
                : '<li class="td-bed-empty"><small>Za to gredo trenutno ni izbranih kultur.</small></li>'
            }
          </ul>
        </div>
      `;
      return `
        <div class="td-plan-dimension" aria-label="Uporabna dolžina gred">
          <span></span><b>↔ DOLŽINA GRED ${formatNumber(model.length, model.length % 1 ? 1 : 0)} M</b><span></span>
        </div>
        ${groups
          .map(
            (group, index) =>
              `${index ? `<div class="td-path">↕ PREHOD ${Math.round(layout.pathWidth * 100)} CM</div>` : ""}${row(
                group.title,
                group.zone,
                group.subtitle,
                group.plants
              )}`
          )
          .join("")}
      `;
    };

    const renderPlanPreview = () => {
      const container = bySelector(root, "[data-td-plan-preview]");
      if (!container) return;
      if (!catalog.length) {
        container.innerHTML = "";
        return;
      }
      const calculation = areaCalculation();
      const model =
        catalog.find((item) => item.area >= calculation.requiredArea) ||
        catalog[catalog.length - 1];
      const layout = layoutForModel(model);
      container.innerHTML = `
        <div class="td-live-plan-heading">
          <div>
            <p class="td-step-label">PREDOGLED NAČRTA ZASADITVE</p>
            <h3>Kako bodo razporejene grede?</h3>
          </div>
          <p>Predogled se samodejno posodablja. Končni načrt bo uporabil točne mere modela, ki ga izberete.</p>
        </div>
        <div class="td-plan td-plan--preview">
          <div class="td-plan-title">
            <span>SEVER ↑</span>
            <div>
              <small>TRENUTNO PRIPOROČENA VELIKOST</small>
              <b>${escapeHtml(model.name)} ${escapeHtml(sizeLabel(model))} · ${bedCountLabel(layout.bedCount)} · ${pathCountLabel(layout.pathCount)}</b>
            </div>
          </div>
          ${plantingRows(model)}
          <div class="td-plan-facts">
            <span><small>ŠIRINA VSAKE GREDE</small><b>${Math.round(layout.bedWidth * 100)} cm</b></span>
            <span><small>UPORABNA DOLŽINA</small><b>${formatNumber(model.length, model.length % 1 ? 1 : 0)} m</b></span>
            <span><small>ŠIRINA PREHODA</small><b>${Math.round(layout.pathWidth * 100)} cm</b></span>
          </div>
        </div>
      `;
    };

    const planNote = () => {
      const melonNote =
        quantities.melon > 0
          ? " Pri melonah načrt predvideva vzgojo ob opori; rast po tleh potrebuje več prostora."
          : "";
      return `Grede so približno enako široke.${melonNote} Shemo prilagodite vratom, namakanju in dejanski smeri severa.`;
    };

    const cropAdvice = () =>
      PLANTS.filter((plant) => quantities[plant.id] > 0)
        .map(
          (plant) => `
            <article>
              <span class="td-advice-icon" aria-hidden="true">${plant.icon}</span>
              <div>
                <small>${plant.inRow} CM V VRSTI • ${plant.betweenRows} CM MED VRSTAMI</small>
                <h4>${plant.name}</h4>
                <p>${plant.tip}</p>
              </div>
            </article>
          `
        )
        .join("");

    const methodologyMarkup = (model) => {
      const calculation = areaCalculation();
      const layout = layoutForModel(model);
      return `
        <section class="td-methodology">
          <p class="td-step-label">PREVERLJIVA METODOLOGIJA</p>
          <h3>Kako smo prišli do priporočila?</h3>
          <div>
            <article><b>1. Površina rastlin</b><p>Za vsako kulturo uporabimo načrtovalsko površino, izpeljano iz priporočenih razdalj sajenja. Skupaj: ${formatNumber(calculation.plantedArea)} m².</p></article>
            <article><b>2. Dostop in nega</b><p>Dodamo 38 % načrtovalske rezerve za prehode in nego, vendar najmanj 1,2 m². To ni uradna norma ali dodatna greda, temveč transparentna ocena prostora za delo.</p></article>
            <article><b>3. Samo dejanski katalog</b><p>Izberemo najmanjšo dejansko velikost iz izbrane Shopify kolekcije, katere površina ni manjša od potrebnih ${formatNumber(calculation.requiredArea)} m².</p></article>
            <article><b>4. Notranja ureditev</b><p>Za širino ${formatNumber(model.width, model.width % 1 ? 1 : 0)} m načrt predvidi ${bedCountLabel(layout.bedCount)} in ${pathCountLabel(layout.pathCount)}.</p></article>
          </div>
          <p>Osnova so načela slovenskih tehnoloških navodil MKGP in KGZS za zelenjadnice v zavarovanih prostorih: primerna sadilna razdalja, dovolj svetlobe, prezračevanje in preprečevanje pregostega sklopa. Končna razdalja je odvisna od sorte, načina vzgoje in lokalnih razmer, zato je rezultat informativna načrtovalska ocena.${settings.mkgpMethodologyUrl ? ` <a href="${escapeHtml(settings.mkgpMethodologyUrl)}" target="_blank" rel="noopener">Tehnološka navodila MKGP ↗</a>` : ""}${settings.kgzsVentilationUrl ? ` • <a href="${escapeHtml(settings.kgzsVentilationUrl)}" target="_blank" rel="noopener">Gradivo KGZS o zračenju ↗</a>` : ""}</p>
        </section>
      `;
    };

    const summaryText = () => {
      const model = selectedModel();
      const variant = bestVariant(model, state.selectedThickness);
      const calculation = areaCalculation();
      const ventilation = ventilationForModel(model);
      const plantText = PLANTS.filter((plant) => quantities[plant.id] > 0)
        .map((plant) => `${plant.name}: ${quantities[plant.id]}`)
        .join(", ");
      return [
        "TIM-DAM – povzetek načrta rastlinjaka",
        `Rastline: ${plantText || "niso izbrane"}`,
        `Prostor za rastline: ${formatNumber(calculation.plantedArea)} m²`,
        `Prehodi in nega: ${formatNumber(calculation.passageArea)} m²`,
        `Izbrani model: ${model.name} ${sizeLabel(model)}`,
        `Polikarbonat: ${thicknessLabel(variant)}`,
        `Prezračevanje: ${ventilation.summary}`,
        `Avtomatsko odpiranje: ${state.autoOpeners ? "da" : "ne"}`,
        `Cena variante: ${formatMoney(variant.priceCents)}`,
        `Izdelek: ${variant.url}`
      ].join("\n");
    };

    const renderFinalResult = () => {
      const model = selectedModel();
      if (!model) return;
      const variant = bestVariant(model, state.selectedThickness);
      const calculation = areaCalculation();
      const minimumSuitable = eligibleModels()[0] || model;
      const ventilation = ventilationForModel(model);
      const layout = layoutForModel(model);
      bySelector(root, "[data-td-final-result]").innerHTML = `
        <section class="td-result-stage">
          <div class="td-result-copy">
            <p class="td-section-kicker">VAŠ NAČRT ZASADITVE</p>
            <h2>Izbrani model ustreza vašemu načrtu.</h2>
            <p>Izbrali ste ${escapeHtml(model.name)} ${escapeHtml(sizeLabel(model))}. Najmanjši primeren model je ${escapeHtml(minimumSuitable.name)} ${escapeHtml(sizeLabel(minimumSuitable))}. Spodnja razporeditev uporablja dejanske mere izbranega modela.</p>
            <div class="td-final-facts">
              <span><small>IZBRANI MODEL</small><b>${escapeHtml(model.name)} ${escapeHtml(sizeLabel(model))}</b></span>
              <span><small>NAČRTOVANA KOLIČINA</small><b>${plantCountLabel(Object.values(quantities).reduce((sum, value) => sum + value, 0))}</b></span>
              <span><small>POLIKARBONAT</small><b>${thicknessLabel(variant)}</b></span>
              <span><small>OKVIRNA CENA</small><b>${formatMoney(variant.priceCents)}</b></span>
            </div>
          </div>
          <div class="td-plan">
            <div class="td-plan-title"><span>SEVER ↑</span><div><small>PRIPOROČENA NOTRANJA UREDITEV</small><b>${bedCountLabel(layout.bedCount)} • ${pathCountLabel(layout.pathCount)}</b></div></div>
            ${plantingRows(model)}
            <div class="td-plan-facts">
              <span><small>ŠIRINA VSAKE GREDE</small><b>${Math.round(layout.bedWidth * 100)} cm</b></span>
              <span><small>UPORABNA DOLŽINA</small><b>${formatNumber(model.length, model.length % 1 ? 1 : 0)} m</b></span>
              <span><small>ŠIRINA PREHODA</small><b>${Math.round(layout.pathWidth * 100)} cm</b></span>
            </div>
            <p>${planNote()}</p>
          </div>
        </section>
        <div class="td-important">
          <strong>Pomembno:</strong>
          <p>Rezultat je strokovno utemeljena načrtovalska ocena, ne univerzalna agronomska norma. Sorta, način vzgoje, opora, širina vrat in želena dostopnost lahko spremenijo končno razporeditev. Zato priporočamo model z nekaj dodatnega prostora za prihodnje sadike.</p>
        </div>
        <section class="td-crop-advice">
          <p class="td-section-kicker">NASVETI ZA VAŠ PRIDELEK</p>
          <h3>Majhne odločitve, boljša letina.</h3>
          <div>${cropAdvice()}</div>
          <p><b>Za vse kulture:</b> zalivajte pri tleh, redno odstranjujte poškodovane dele in rastlinjak vsak dan prilagodite temperaturi ter vlagi. Prezračevanje: ${escapeHtml(ventilation.summary)}.</p>
        </section>
        <div class="td-purchase-card">
          <div>
            <small>PRIPOROČENA OPREMA</small>
            <b>${escapeHtml(ventilation.title)}${state.autoOpeners ? " + avtomatsko odpiranje" : ""}</b>
          </div>
          <a href="${escapeHtml(variant.url)}" target="_blank" rel="noopener">Kupi izbrano varianto na tim-dam.com ↗</a>
        </div>
        <p class="td-delivery-note">Aktualno ceno dostave, morebitno brezplačno dostavo in možnost osebnega prevzema preverite na strani izbranega izdelka.</p>
      `;
      bySelector(root, "[data-td-copy]").dataset.summary = summaryText();
    };

    const renderShowcase = () => {
      const container = bySelector(root, "[data-td-showcase]");
      if (!catalog.length) {
        container.innerHTML = setupMessage();
        return;
      }
      const selected = [];
      featuredRules.slice(0, 4).forEach((rule) => {
        const dimensions = findDimensions(rule.size);
        const modelKeyword = String(rule.modelKeyword || "").trim().toLocaleLowerCase("sl-SI");
        const match = catalog.find(
          (model) => {
            const variant = model.variants[rule.thickness];
            if (!variant) return false;
            const productMatches = rule.handle
              ? variant.handle === rule.handle
              : !modelKeyword || model.name.toLocaleLowerCase("sl-SI").includes(modelKeyword);
            const sizeMatches =
              !dimensions ||
              (Math.abs(model.width - dimensions.width) < 0.01 &&
                Math.abs(model.length - dimensions.length) < 0.01);
            return productMatches && sizeMatches;
          }
        );
        if (match && !selected.some((item) => item.model.id === match.id)) {
          selected.push({ model: match, thickness: rule.thickness, badge: rule.badge });
        }
      });
      if (!featuredRules.length) {
        catalog
          .slice(0, 4)
          .forEach((model, index) => {
            selected.push({
              model,
              thickness: bestVariant(model, index % 2 ? 6 : 4).thickness,
              badge: index === 0 ? "Priporočamo" : ""
            });
          });
      }
      container.innerHTML = selected
        .map(({ model, thickness, badge }) =>
          modelCard(model, { thickness, badge })
        )
        .join("");
      if (!selected.length) {
        container.innerHTML = setupMessage();
      }
    };

    allBySelector(root, "[data-td-mode]").forEach((button) => {
      button.addEventListener("click", () => setMode(button.dataset.tdMode));
    });
    bySelector(root, ".td-mode-switch").addEventListener("keydown", (event) => {
      if (!["ArrowLeft", "ArrowRight"].includes(event.key)) return;
      event.preventDefault();
      const nextMode = state.mode === "quick" ? "exact" : "quick";
      setMode(nextMode);
      bySelector(root, `[data-td-mode="${state.mode}"]`)?.focus();
    });
    allBySelector(root, "[data-td-audience]").forEach((button) => {
      button.addEventListener("click", () => {
        state.quickArea = numberValue(button.dataset.area);
        allBySelector(root, "[data-td-audience]").forEach((item) => item.classList.remove("is-selected"));
        button.classList.add("is-selected");
        showQuickStep(2);
      });
    });
    allBySelector(root, "[data-td-space]").forEach((button) => {
      button.addEventListener("click", () => {
        state.quickSpace = button.dataset.tdSpace;
        state.quickMaxArea = numberValue(button.dataset.maxArea);
        renderQuickResults();
        showQuickStep(3);
      });
    });
    bySelector(root, "[data-td-quick-back]").addEventListener("click", () => showQuickStep(1));
    bySelector(root, "[data-td-quick-restart]").addEventListener("click", () => {
      state.quickArea = 8;
      state.quickSpace = "";
      state.quickMaxArea = 0;
      state.quickCapacityShortfall = false;
      state.quickSpaceShortfall = false;
      allBySelector(root, "[data-td-audience]").forEach((item) => item.classList.remove("is-selected"));
      showQuickStep(1);
    });

    root.addEventListener("click", (event) => {
      const plus = event.target.closest("[data-plant-plus]");
      const minus = event.target.closest("[data-plant-minus]");
      const modelButton = event.target.closest("[data-select-model]");
      const thicknessButton = event.target.closest("[data-thickness]");
      const modelToggle = event.target.closest("[data-td-toggle-models]");
      if (plus) updatePlant(plus.dataset.plantPlus, 1);
      if (minus) updatePlant(minus.dataset.plantMinus, -1);
      if (modelButton) {
        state.selectedModelId = modelButton.dataset.selectModel;
        renderModelResults();
      }
      if (thicknessButton) {
        state.selectedThickness = Number(thicknessButton.dataset.thickness);
        renderEquipment();
      }
      if (modelToggle) {
        state.showAllModels = !state.showAllModels;
        renderModelResults();
      }
    });
    root.addEventListener("keydown", (event) => {
      const modelChoice = event.target.closest(".td-model-card--compact[data-select-model]");
      if (!modelChoice || !["Enter", " "].includes(event.key)) return;
      event.preventDefault();
      modelChoice.click();
    });
    root.addEventListener("change", (event) => {
      const plantInput = event.target.closest("[data-plant-input]");
      if (plantInput) setPlantQuantity(plantInput.dataset.plantInput, plantInput.value);
    });

    const budget = bySelector(root, "[data-td-budget]");
    budget.addEventListener("input", () => {
      state.budget = Number(budget.value) * 100;
      bySelector(root, "[data-td-budget-label]").textContent = `do ${formatMoney(state.budget)}`;
    });
    bySelector(root, "[data-td-no-budget]").addEventListener("change", (event) => {
      state.noBudget = event.target.checked;
      budget.disabled = state.noBudget;
      bySelector(root, "[data-td-budget-label]").textContent = state.noBudget
        ? "Še ni določen"
        : `do ${formatMoney(state.budget)}`;
    });
    bySelector(root, "[data-td-show-models]").addEventListener("click", () => {
      if (!Object.values(quantities).some((value) => value > 0)) {
        announce("Izberite vsaj eno rastlino.");
        return;
      }
      state.budgetLimit = state.noBudget ? 0 : state.budget;
      state.showAllModels = false;
      const budgetFilter = bySelector(root, "[data-td-budget-filter]");
      if (
        state.budgetLimit > 0 &&
        !budgetFilter.querySelector(`option[value="${state.budgetLimit}"]`)
      ) {
        budgetFilter.insertAdjacentHTML(
          "beforeend",
          `<option value="${state.budgetLimit}">Do ${formatMoney(state.budgetLimit)}</option>`
        );
      }
      budgetFilter.value = state.budgetLimit > 0 ? String(state.budgetLimit) : "0";
      renderModelResults();
      showExactStep(2);
    });
    bySelector(root, "[data-td-width-filter]").addEventListener("change", (event) => {
      state.widthLimit = numberValue(event.target.value);
      state.showAllModels = false;
      renderModelResults();
    });
    bySelector(root, "[data-td-length-filter]").addEventListener("change", (event) => {
      state.lengthLimit = numberValue(event.target.value);
      state.showAllModels = false;
      renderModelResults();
    });
    bySelector(root, "[data-td-budget-filter]").addEventListener("change", (event) => {
      state.budgetLimit = Number(event.target.value);
      state.showAllModels = false;
      renderModelResults();
    });
    allBySelector(root, "[data-td-exact-back]").forEach((button) => {
      button.addEventListener("click", () => showExactStep(Number(button.dataset.tdExactBack)));
    });
    bySelector(root, "[data-td-to-equipment]").addEventListener("click", () => {
      if (!selectedModel()) {
        announce("Najprej izberite model iz kataloga.");
        return;
      }
      renderEquipment();
      showExactStep(3);
    });
    bySelector(root, "[data-td-equipment]").addEventListener("change", (event) => {
      if (event.target.matches("[data-auto-opener]")) {
        state.autoOpeners = event.target.checked;
      }
    });
    bySelector(root, "[data-td-to-result]").addEventListener("click", () => {
      renderFinalResult();
      showExactStep(4);
    });
    bySelector(root, "[data-td-copy]").addEventListener("click", async (event) => {
      const button = event.currentTarget;
      const summary = button.dataset.summary || summaryText();
      try {
        await navigator.clipboard.writeText(summary);
        button.firstChild.textContent = "Povzetek je kopiran ";
        announce("Povzetek je kopiran. Lahko ga prilepite v sporočilo ali e-pošto.");
      } catch {
        const textarea = document.createElement("textarea");
        textarea.value = summary;
        textarea.setAttribute("readonly", "");
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.select();
        const copied = document.execCommand("copy");
        textarea.remove();
        if (copied) {
          button.firstChild.textContent = "Povzetek je kopiran ";
          announce("Povzetek je kopiran. Lahko ga prilepite v sporočilo ali e-pošto.");
        } else {
          announce("Kopiranje ni uspelo. Poskusite znova ali odprite stran v brskalniku Safari oziroma Chrome.");
        }
      }
    });

    renderMethodTable();
    renderPlants();
    updateAreaSummary();
    populateFilters();
    renderShowcase();
    if (!catalog.length) announce("V nastavitvah sekcije izberite kolekcijo rastlinjakov.");
  };

  const boot = () => {
    document.documentElement.classList.toggle(
      "td-calculator-active",
      Boolean(document.querySelector("[data-td-calculator]"))
    );
    suppressExternalDeliveryBar();
    document.querySelectorAll("[data-td-calculator]").forEach((root) => {
      if (root.dataset.initialized === "true") return;
      root.dataset.initialized = "true";
      initialize(root);
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
  document.addEventListener("shopify:section:load", boot);
  document.addEventListener("shopify:section:unload", () => {
    requestAnimationFrame(boot);
  });
})();
